import * as React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/utils'
import {
  DROPDOWN_LIST_CLASS,
  DROPDOWN_PANEL_CLASS,
  ZOOM_LISTBOX_ID,
} from '../constants/zoom-dropdown'
import { zoomOptionKey } from '../helpers/zoom-menu-options'
import type { ZoomMenuOption } from '../types/zoom-menu-option'
import { ZoomOptionRow } from './zoom-option-row'

type ZoomDropdownProps = {
  /** `portal`: fixed-positioned under `document.body`; `inline`: absolutely below the control. */
  mode: 'portal' | 'inline'
  fixedStyle?: React.CSSProperties
  panelRef: React.Ref<HTMLDivElement>
  listRef: React.Ref<HTMLDivElement>
  menuOptions: ZoomMenuOption[]
  highlightIndex: number
  parsedDraft: number | null
  /** Don’t tint a preset as “current %” while a non-preset row is hovered (e.g. Fit resets draft to 100%). */
  suppressPresetMatchHighlight: boolean
  fitToSpreadDisabled: boolean
  zoomSelectionRowDisabled: boolean
  onHighlight: (index: number) => void
  onActivate: (index: number) => void
}

/** Disabled state for the rows that can be disabled; undefined (no attribute) for the others. */
function resolveOptionDisabled(
  opt: ZoomMenuOption,
  fitToSpreadDisabled: boolean,
  zoomSelectionRowDisabled: boolean
): boolean | undefined {
  if (opt.kind === 'fit') return fitToSpreadDisabled
  if (opt.kind === 'zoomSelection') return zoomSelectionRowDisabled

  return undefined
}

/** The zoom preset listbox panel. */
export function ZoomDropdown({
  mode,
  fixedStyle,
  panelRef,
  listRef,
  menuOptions,
  highlightIndex,
  parsedDraft,
  suppressPresetMatchHighlight,
  fitToSpreadDisabled,
  zoomSelectionRowDisabled,
  onHighlight,
  onActivate,
}: ZoomDropdownProps) {
  const list = (
    <div ref={listRef} className={DROPDOWN_LIST_CLASS}>
      {menuOptions.map((opt, index) => (
        <ZoomOptionRow
          key={zoomOptionKey(opt)}
          option={opt}
          index={index}
          highlighted={highlightIndex === index}
          disabled={resolveOptionDisabled(opt, fitToSpreadDisabled, zoomSelectionRowDisabled)}
          matchesDraft={
            opt.kind === 'preset' &&
            !suppressPresetMatchHighlight &&
            parsedDraft != null &&
            Math.round(parsedDraft) === opt.percent
          }
          onHighlight={onHighlight}
          onActivate={onActivate}
        />
      ))}
    </div>
  )

  if (mode === 'portal') {
    return createPortal(
      <div
        ref={panelRef}
        id={ZOOM_LISTBOX_ID}
        role="listbox"
        data-zoom-dropdown-root
        style={fixedStyle}
        className={DROPDOWN_PANEL_CLASS}
      >
        {list}
      </div>,
      document.body
    )
  }

  return (
    <div
      id={ZOOM_LISTBOX_ID}
      role="listbox"
      className={cn('absolute top-[calc(100%+0.25rem)] left-0 z-50', DROPDOWN_PANEL_CLASS)}
    >
      {list}
    </div>
  )
}
